import { RefObject, useEffect } from 'react';

type Handler = (event: MouseEvent | TouchEvent) => void;

export default function useClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: Handler,
  {
    exceptionsRefs = [],
    shouldTrigger = () => true,
  }: {
    exceptionsRefs?: RefObject<HTMLElement>[];
    shouldTrigger?: (event: MouseEvent | TouchEvent) => boolean;
  } = {},
): void {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const el = ref.current;
      const target = event.target as Node;

      // Check if the click is inside the main element or any of the exception elements
      if (
        !el ||
        el.contains(target) ||
        exceptionsRefs.some(
          (exceptRef) =>
            exceptRef.current && exceptRef.current.contains(target),
        )
      ) {
        return;
      }

      // Check the custom condition
      if (shouldTrigger(event)) {
        handler(event);
      }
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, exceptionsRefs, shouldTrigger]);
}
